<template>
  <div>
    <transition name="fade">
      <Alert type="error" :message="error" v-if="error" />
    </transition>

    <Card has-empty-title>
      <button slot="actions" class="slds-button slds-button_brand" @click="addEmptyRow" v-if="editable">
        <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
          <use xlink:href="../../../../../assets/symbols.svg#add"></use>
        </svg>
        {{ $t('campaignDesigner.contentSelection.addTemplate') }}
      </button>

      <!-- Rows -->
      <transition-group name="slide-fade-down">
        <template v-for="(template, index) in templates">
          <Card class="slds-m-top--medium" :key="index" additional-classes="slds-card_boundary" has-empty-title>
            <template #actions>
               <button class="slds-button slds-button_destructive" @click="deleteTemplate(index)" v-if="editable">
                <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
                  <use xlink:href="../../../../../assets/symbols.svg#delete"></use>
                </svg>
                {{ $t('campaignDesigner.contentSelection.deleteTemplate') }}
              </button>
            </template>

            <div class="row">
              <!-- Format Id -->
              <div class="slds-form-element col">
                <div class="slds-form-element slds-size--medium">
                  <Picklist
                    :label="$t('campaignDesigner.contentSelection.formatId.label')"
                    :options="formatIds"
                    :validation="['required']"
                    :ref="`input_formatId_${index}`"
                    class="slds-m-right--small"
                    small
                    v-if="editable"
                    v-model="template.formatId" />

                  <OutputField
                    :label="$t('campaignDesigner.contentSelection.formatId.label')"
                    :options="formatIds"
                    :value="template.formatId"
                    type="picklist"
                    v-else />
                </div>
              </div>

              <!-- Template type -->
              <div class="slds-form-element col">
                <div class="slds-form-element slds-size--medium">
                  <Picklist
                    :label="$t('campaignDesigner.contentSelection.templateType.label')"
                    :options="templateTypes"
                    :validation="['required']"
                    :ref="`input_templateType_${index}`"
                    class="slds-m-right--small"
                    labels-prefix="campaignDesigner.contentSelection.templateType"
                    small
                    v-if="editable"
                    v-model="template.templateType" />

                  <OutputField
                    :label="$t('campaignDesigner.contentSelection.templateType.label')"
                    :options="templateTypes"
                    :value="template.templateType"
                    labels-prefix="campaignDesigner.contentSelection.templateType"
                    type="picklist"
                    v-else />
                </div>
              </div>

              <!-- Template name -->
              <div class="slds-form-element col">
                <div class="slds-form-element slds-size--medium">
                  <Input
                    :label="$t('campaignDesigner.contentSelection.templateName')"
                    :validation="['required']"
                    class="slds-m-right--small"
                    :ref="`input_templateName_${index}`"
                    v-if="editable"
                    v-model="template.templateName" />

                  <OutputField
                    :label="$t('campaignDesigner.contentSelection.templateName')"
                    :value="template.templateName"
                    v-else />
                </div>
              </div>

              <!-- HTML Image -->
              <div class="slds-form-element col">
                <div class="slds-form-element slds-size--medium">
                  <Input
                    :label="$t('campaignDesigner.contentSelection.htmlImg')"
                    :validation="['required']"
                    class="slds-m-right--small"
                    :ref="`input_htmlImg_${index}`"
                    v-if="editable"
                    v-model="template.htmlImg" />

                    <OutputField
                      :label="$t('campaignDesigner.contentSelection.htmlImg')"
                      :value="template.htmlImg"
                      v-else />
                </div>
              </div>
            </div>

            <Card additional-classes="slds-card_boundary slds-m-top--medium" :title="$t('campaignDesigner.contentSelection.htmlTemplate')">
              <transition name="fade">
                <Alert type="error" :message="template.error" v-if="template.error" />
              </transition>
              <template #actions>
                <button class="slds-button slds-button_brand" @click="updateTemplate(index)" v-if="editable">
                  <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
                    <use xlink:href="../../../../../assets/symbols.svg#refresh"></use>
                  </svg>
                  {{ $t('campaignDesigner.contentSelection.updateTemplate') }}
                </button>
              </template>
              <div class="row">
                <!-- HTML Preview -->
                <div class="slds-form-element col">
                  <div class="slds-form-element">
                    <label class="slds-form-element__label">
                      {{$t('campaignDesigner.contentSelection.htmlTemplate')}} ({{template.htmlPreview.length}} / 4000)
                    </label>
                    <ace-editor
                      @init="editorInit"
                      v-model="template.htmlPreview"
                      height="300"
                      lang="html"
                      theme="monokai"
                      width="100%"
                      :options="{readOnly: !editable}"
                      :ref="`html_${index}`" />
                  </div>
                </div>
              </div>
              <div class="row slds-m-top--small">
                <div class="slds-form-element col">
                  <div class="slds-form-element">
                    <label class="slds-form-element__label">{{$t('campaignDesigner.contentSelection.htmlTemplatePreview')}}</label>
                    <div class="preview" v-html="template.updatedHtmlPreview" />
                  </div>
                </div>
              </div>
            </Card>
          </Card>
        </template>
      </transition-group>
    </Card>
  </div>
</template>
<script>
import _ from 'lodash'
import AceEditor from 'vue2-ace-editor'
import Alert from '../../../../slds/Alert'
import Card from '../../../../slds/Card'
import Input from '../../../../slds/Input'
import Picklist from '../../../../slds/Picklist'
import OutputField from '../../../../slds/OutputField'
import TextArea from '../../../../slds/TextArea'
export default {
  components: {
    AceEditor,
    Alert,
    Card,
    Input,
    OutputField,
    Picklist,
    TextArea
  },
  props: {
    editable: {
      type: Boolean,
      default: false,
      required: true
    },
    value: {
      type: Object,
      default: () => {},
      required: true
    }
  },
  data () {
    return {
      error: '',
      formatIds: [],
      templates: [],
      templateTypes: []
    }
  },
  created () {
    const { ddsFormatId, ddsTemplateType } = this.$store.getters['settings/settings']
    this.formatIds = ddsFormatId
    this.templateTypes = ddsTemplateType
    this.resetValidation()
    this.mapValue(this.value)
  },
  activated () {
    this.mapValue(this.value)
  },
  methods: {
    resetValidation () {
      Object.values(this.$refs).forEach(ref => {
        if (ref.clearValidationError) {
          ref.clearValidationError()
        }
      })
    },
    addEmptyRow () {
      this.error = ''
      this.templates.push({
        error: '',
        formatId: '',
        htmlImg: '',
        htmlPreview: '',
        templateName: '',
        templateType: '',
        updatedHtmlPreview: ''
      })
    },
    deleteTemplate (index) {
      this.templates.splice(index, 1)
    },
    editorInit () {
      require('brace/ext/language_tools')
      require('brace/mode/html')
      require('brace/theme/monokai')
      require('brace/snippets/html')
    },
    getSectionToSave () {
      const mapped = this.templates.map((template, index) => {
        return {
          formatId: {
            options: this.formatIds,
            selected: template.formatId
          },
          htmlImg: {
            selected: template.htmlImg
          },
          htmlPreview: {
            selected: template.htmlPreview || ''
          },
          templateId: {
            selected: (index + 1)
          },
          templateName: {
            selected: template.templateName
          },
          templateType: {
            options: this.templateTypes,
            selected: template.templateType
          },
        }
      })
      return mapped
    },
    mapValue (value) {
      this.templates = []

      const templates = value.templates.selected

      templates.forEach(val=> {
        const {
          htmlImg,
          htmlPreview,
          formatId,
          templateName,
          templateType
        } = val

        this.templates.push({
          htmlImg: htmlImg.selected || '',
          htmlPreview: htmlPreview.selected || '',
          formatId: formatId.selected || '',
          templateName: templateName.selected || '',
          templateType: templateType.selected || '',
          updatedHtmlPreview: (htmlPreview.selected || '')
        })
      });
    },
    updateTemplate (index) {
      const html = this.templates[index].htmlPreview
      this.templates[index].updatedHtmlPreview = (html || '')
      this.$forceUpdate()
    },
    validate () {
      this.error = ''
      let valid = true

      if (!this.templates.length) {
        valid = false
        this.error = this.$t('campaignDesigner.contentSelection.validation.email.required')
      }

      if (valid) {
        const refs = Object.entries(this.$refs)
        valid = refs
          .filter(([k,v]) => k.startsWith('input_'))
          .map(r => r[1])
          .flat()
          .map(v => v.validate())
          .every(v => !!v)

        valid &= refs
          .filter(([k,v]) => k.startsWith('html'))
          .map(([k,v]) => {
            const component = v[0]

            const [prefix, index] = k.split('_')
            if (component.value.length > 4000) {
              this.templates[index].error = this.$t('validation.maxLength', { maxValue: 4000 })
              return false
            } else {
              this.templates[index].error = ''
              return true
            }
          })
          .every(v => !!v)
      }

      return valid
    }
  },
  watch: {
    value (val) {
      this.resetValidation()
      this.mapValue(val)
    }
  }
}
</script>
<style lang="scss" scoped>
  .add-button {
    align-self: flex-end;
  }

  .row {
    display: flex;
    flex-direction: row;

    & > * {
      align-self: flex-end;
      flex-grow: 1;
    }
  }

  .button-container {
    display: flex;
    flex-direction: row;
  }

  .delete-button:not(:disabled) {
    color: #C23933;
  }

  .preview {
    background-color: white;
    border: 1px solid #dddbda;
    border-radius: 0.25rem;
    overflow-x: auto;
    height: 300px;
  }
</style>
